import classNames from "classnames";
import React, { CSSProperties, FC, UIEvent } from "react";
import { LuCheck, LuPlus } from "react-icons/lu";

import { Button } from "../button";
import { Checkbox, CheckboxSize } from "../checkbox";
import { isSelectedOption } from "./Select.helpers";
import {
  OptionContent,
  SelectEmptyState,
  SelectFooter,
  SelectSearch,
  SelectSpinner,
} from "./Select.parts";
import {
  SelectAllConfig,
  SelectFooterConfig,
  SelectOption,
  SelectProps,
  SelectSearchConfig,
  SelectTab,
  SelectVirtualizationConfig,
} from "./Select.types";

type NormalizedSearchConfig =
  | (Required<Pick<SelectSearchConfig, "placeholder" | "mode">> &
      SelectSearchConfig)
  | null;

type VisibleRange = {
  startIndex: number;
  endIndex: number;
  topPadding: number;
  bottomPadding: number;
};

type SelectMenuProps = {
  menuRef: React.RefObject<HTMLDivElement>;
  listRef: React.RefObject<HTMLUListElement>;
  menuClassName?: string;
  menuStyle: CSSProperties;
  listboxId: string;
  optionIdPrefix: string;
  isContentMenu: boolean;
  isEmbedded: boolean;
  tabs?: SelectTab[];
  activeTab?: string;
  onTabChange?: (value: string) => void;
  createAction?: SelectProps["createAction"];
  closeMenu: () => void;
  searchConfig: NormalizedSearchConfig;
  searchValue: string;
  onSearchChange: (value: string) => void;
  listHeader?: React.ReactNode;
  selectAll?: SelectAllConfig | null;
  selectAllChecked: boolean;
  selectedCount: number;
  selectAllDisabled: boolean;
  onSelectAllToggle: () => void;
  menuMaxHeight: number;
  onListScroll: (event: UIEvent<HTMLUListElement>) => void;
  loading?: boolean;
  loadingMore?: boolean;
  emptyLabel: string;
  emptyState?: SelectProps["emptyState"];
  allowMulti: boolean;
  currentSelectedOptions: SelectOption[];
  visibleOptions: SelectOption[];
  visibleStartIndex: number;
  visibleRange: VisibleRange;
  virtualConfig: Required<SelectVirtualizationConfig>;
  renderOption?: SelectProps["renderOption"];
  renderOptionPreview?: SelectProps["renderOptionPreview"];
  hoveredOption: SelectOption | null;
  onOptionHover: (option: SelectOption | null) => void;
  onOptionClick: (option: SelectOption) => void;
  footer?: SelectFooterConfig;
  clearSelection: () => void;
  doneSelection: () => void;
  filteredOptionsCount: number;
  checkboxSize?: CheckboxSize;
};

export const SelectMenu: FC<SelectMenuProps> = ({
  menuRef,
  listRef,
  menuClassName,
  menuStyle,
  listboxId,
  optionIdPrefix,
  isContentMenu,
  isEmbedded,
  tabs,
  activeTab,
  onTabChange,
  createAction,
  closeMenu,
  searchConfig,
  searchValue,
  onSearchChange,
  listHeader,
  selectAll,
  selectAllChecked,
  selectedCount,
  selectAllDisabled,
  onSelectAllToggle,
  menuMaxHeight,
  onListScroll,
  loading,
  loadingMore,
  emptyLabel,
  emptyState,
  allowMulti,
  currentSelectedOptions,
  visibleOptions,
  visibleStartIndex,
  visibleRange,
  virtualConfig,
  renderOption,
  renderOptionPreview,
  hoveredOption,
  onOptionHover,
  onOptionClick,
  footer,
  clearSelection,
  doneSelection,
  filteredOptionsCount,
  checkboxSize,
}) => (
  <div
    ref={menuRef}
    className={classNames(
      "z-50 bg-white font-Manrope",
      !isEmbedded &&
        "rounded-[10px] border border-[#E5E7EB] shadow-[0_8px_24px_rgba(0,0,0,0.12)]",
      renderOptionPreview ? "overflow-visible" : "overflow-hidden",
      isContentMenu && "absolute left-0 top-full mt-1.5",
      menuClassName,
    )}
    style={menuStyle}
    onMouseLeave={() => onOptionHover(null)}>
    {(tabs || createAction) && (
      <div className={"border-b border-[#F0F1F4] px-2 pb-[7px] pt-2"}>
        {tabs && (
          <div
            className={classNames(
              "grid grid-cols-2 gap-1",
              createAction && "mb-2",
            )}>
            {tabs.map((tab) => (
              <Button
                key={tab.value}
                type={"button"}
                variant={"ghost"}
                size={"sm"}
                autoWidth
                className={classNames(
                  "!h-auto rounded-[7px] px-2.5 py-[7px] text-[12.5px] font-bold transition-colors",
                  activeTab === tab.value
                    ? "bg-[#F0EDFF] text-[#6B4EFF]"
                    : "bg-transparent text-[#9097A8] hover:bg-[#F8F7FE] hover:text-[#6B4EFF]",
                )}
                onClick={() => onTabChange?.(tab.value)}>
                {tab.label}
                {typeof tab.count === "number" && (
                  <span className={"ml-1 text-[11px]"}>{tab.count}</span>
                )}
              </Button>
            ))}
          </div>
        )}
        {createAction && (
          <Button
            type={"button"}
            variant={"ghost"}
            size={"sm"}
            fullWidth
            leftIcon={
              <span
                className={
                  "flex h-5 w-5 items-center justify-center rounded-full border border-[#6B4EFF]"
                }>
                {createAction.icon ?? <LuPlus size={13} />}
              </span>
            }
            className={
              "!h-auto justify-start rounded-lg px-2 py-2 text-left text-sm font-bold text-[#6B4EFF] hover:bg-[#F4F2FF]"
            }
            onClick={() => {
              createAction.onClick();
              closeMenu();
            }}>
            {createAction.label}
          </Button>
        )}
      </div>
    )}

    {searchConfig && (
      <SelectSearch
        search={searchConfig}
        value={searchValue}
        onChange={onSearchChange}
        embedded={isEmbedded}
      />
    )}

    <div
      className={classNames(
        isEmbedded &&
          "overflow-hidden rounded-[11px] border border-[#ECEFF3] bg-white",
      )}>
      {listHeader}
      {allowMulti && selectAll?.enabled && (
        <div
          role={"checkbox"}
          aria-checked={selectAllChecked}
          aria-disabled={selectAllDisabled || undefined}
          tabIndex={selectAllDisabled ? -1 : 0}
          className={
            "flex w-full cursor-pointer items-center gap-2 border-b border-[#ECEFF3] px-3 py-2.5 text-left transition-colors hover:bg-[#F8F7FE] aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
          }
          onClick={() => {
            if (!selectAllDisabled) {
              onSelectAllToggle();
            }
          }}
          onKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== " ") {
              return;
            }

            event.preventDefault();

            if (!selectAllDisabled) {
              onSelectAllToggle();
            }
          }}>
          <Checkbox
            checked={selectAllChecked}
            disabled={selectAllDisabled}
            presentation
            size={checkboxSize}
          />
          <span className={"text-sm font-medium text-[#6B4EFF]"}>
            {selectAllChecked
              ? selectAll.deselectLabel ?? "Deselect all"
              : selectAll.selectLabel ?? "Select all"}
          </span>
          {selectedCount > 0 && (
            <span className={"ml-auto text-xs font-medium text-[#8A90A2]"}>
              {selectAll.selectedCountLabel
                ? selectAll.selectedCountLabel(selectedCount)
                : selectedCount === 1
                ? "1 selected"
                : `${selectedCount} selected`}
            </span>
          )}
        </div>
      )}

      <ul
        ref={listRef}
        id={listboxId}
        role={"listbox"}
        aria-multiselectable={allowMulti || undefined}
        tabIndex={-1}
        className={classNames("overflow-y-auto", isEmbedded ? "p-0" : "p-1")}
        style={{ maxHeight: menuMaxHeight }}
        onScroll={onListScroll}>
        {loading && !loadingMore ? (
          <li
            className={
              "flex items-center justify-center gap-2 px-3 py-6 text-sm text-[#6B7185]"
            }>
            <SelectSpinner />
            Loading
          </li>
        ) : filteredOptionsCount === 0 ? (
          <li
            className={
              "flex items-center justify-center px-[14px] pb-[14px] pt-4"
            }>
            <SelectEmptyState emptyLabel={emptyLabel} emptyState={emptyState} />
          </li>
        ) : (
          <>
            {virtualConfig.enabled && visibleRange.topPadding > 0 && (
              <li aria-hidden style={{ height: visibleRange.topPadding }} />
            )}
            {visibleOptions.map((option, visibleIndex) => {
              const optionIndex = visibleStartIndex + visibleIndex;
              const selected = isSelectedOption(option, currentSelectedOptions);

              return (
                <li
                  key={option.value}
                  id={`${optionIdPrefix}-${optionIndex}`}
                  role={"option"}
                  aria-selected={selected}
                  aria-disabled={option.disabled || undefined}
                  className={classNames(
                    "flex w-full items-center gap-3 overflow-hidden text-left font-Manrope text-sm transition-colors",
                    option.disabled
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer",
                    isEmbedded
                      ? "rounded-none border-b border-[#F4F5F7] px-3 py-2 last:border-b-0"
                      : "rounded-lg px-3 py-2",
                    selected ? "bg-[#F6F4FF]" : "hover:bg-[#F8F7FE]",
                  )}
                  style={{
                    height: virtualConfig.enabled
                      ? virtualConfig.itemHeight
                      : undefined,
                    minHeight: virtualConfig.enabled
                      ? virtualConfig.itemHeight
                      : undefined,
                  }}
                  onMouseEnter={() => onOptionHover(option)}
                  onClick={() => {
                    if (!option.disabled) {
                      onOptionClick(option);
                    }
                  }}>
                  {allowMulti && (
                    <Checkbox
                      checked={selected}
                      presentation
                      size={checkboxSize}
                    />
                  )}
                  {renderOption ? (
                    renderOption(option, {
                      selected,
                      disabled: !!option.disabled,
                      active: selected,
                      isMulti: allowMulti,
                    })
                  ) : (
                    <OptionContent option={option} />
                  )}
                  {!allowMulti && selected && (
                    <LuCheck
                      size={16}
                      strokeWidth={2.6}
                      className={"ml-auto shrink-0 text-[#6B4EFF]"}
                    />
                  )}
                </li>
              );
            })}
            {virtualConfig.enabled && visibleRange.bottomPadding > 0 && (
              <li aria-hidden style={{ height: visibleRange.bottomPadding }} />
            )}
          </>
        )}
        {loadingMore && (
          <li
            className={
              "flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold text-[#6B7185]"
            }>
            <SelectSpinner />
            Loading more
          </li>
        )}
      </ul>

      <SelectFooter
        footer={footer}
        selectedCount={currentSelectedOptions.length}
        clear={clearSelection}
        done={doneSelection}
        close={closeMenu}
      />
    </div>
    {hoveredOption && renderOptionPreview && (
      <div className={"absolute left-[calc(100%+8px)] top-0 z-[1]"}>
        {renderOptionPreview(hoveredOption)}
      </div>
    )}
  </div>
);
